// src/theme/index.js
import { defineConfig, createSystem, defaultConfig } from "@chakra-ui/react";
import "@fontsource-variable/montserrat";
import "@fontsource-variable/outfit";
import "@fontsource/fredoka/latin-300.css";
import "@fontsource/fredoka/latin-400.css";
import "@fontsource/fredoka/latin-500.css";
import "@fontsource/fredoka/latin-600.css";
import "@fontsource/fredoka/latin-700.css";
import "@fontsource/nunito-sans/latin-200.css";
import "@fontsource/nunito-sans/latin-300.css";
import "@fontsource/nunito-sans/latin-400.css";
import "@fontsource/nunito-sans/latin-500.css";
import "@fontsource/nunito-sans/latin-600.css";
import "@fontsource/nunito-sans/latin-700.css";
import "@fontsource/nunito-sans/latin-800.css";
import "@fontsource/nunito-sans/latin-900.css";

import { pressableButtonRecipe } from "./recipes";

const DASHBOARD_TINTS = [
  "gray",
  "red",
  "orange",
  "yellow",
  "green",
  "teal",
  "blue",
  "cyan",
  "purple",
  "pink",
];

const dashboardTintVars = (palette) => ({
  "--dashboard-bg-canvas": `var(--chakra-colors-${palette}-950)`,
  "--dashboard-bg-panel": `var(--chakra-colors-${palette}-900)`,
  "--dashboard-bg-surface": `var(--chakra-colors-${palette}-900)`,
  "--dashboard-bg-surface-muted": `var(--chakra-colors-${palette}-800)`,
  "--dashboard-bg-surface-subtle": `var(--chakra-colors-${palette}-700)`,
  "--dashboard-bg-dialog": `var(--chakra-colors-${palette}-900)`,
  "--dashboard-bg-input": `var(--chakra-colors-${palette}-800)`,
  "--dashboard-bg-popover": `var(--chakra-colors-${palette}-800)`,
  "--dashboard-bg-tab-list": `var(--chakra-colors-${palette}-800)`,
  "--dashboard-bg-tab-hover": "var(--chakra-colors-whiteAlpha-200)",
  "--dashboard-bg-tab-selected": `var(--chakra-colors-${palette}-700)`,
  "--dashboard-bg-choice-selected": `var(--chakra-colors-${palette}-700)`,
  "--dashboard-fg-default": "var(--chakra-colors-gray-200)",
  "--dashboard-fg-muted": "var(--chakra-colors-gray-400)",
  "--dashboard-fg-subtle": "var(--chakra-colors-gray-500)",
  "--dashboard-border-default": `var(--chakra-colors-${palette}-600)`,
  "--dashboard-border-muted": `var(--chakra-colors-${palette}-700)`,
  "--dashboard-tag-bg": `var(--chakra-colors-${palette}-700)`,
  "--dashboard-tag-fg": "var(--chakra-colors-gray-100)",
  "--dashboard-tag-border": `var(--chakra-colors-${palette}-500)`,
});

const tintGlobalCss = {
  ":root, [data-dashboard-tint='gray']": dashboardTintVars("gray"),
  ...Object.fromEntries(
    DASHBOARD_TINTS.filter((t) => t !== "gray").map((tint) => [
      `[data-dashboard-tint='${tint}']`,
      dashboardTintVars(tint),
    ]),
  ),
};

const config = defineConfig({
  globalCss: {
    body: { fontFamily: "body" },
    ...tintGlobalCss,
  },
  theme: {
    tokens: {
      fonts: {
        heading: { value: "'Outfit Variable', 'Outfit', sans-serif" },
        body: { value: "'Montserrat Variable', 'Montserrat', sans-serif" },
        display: { value: "'Fredoka', 'Outfit Variable', 'Outfit', sans-serif" },
        bodyAlt: { value: "'Nunito Sans', 'Montserrat Variable', 'Montserrat', sans-serif" },
      },
      fontSizes: {
        display: { value: "clamp(2.75rem, 6vw, 4.5rem)" },
        section: { value: "1.5rem" },
        eyebrow: { value: "0.75rem" },
      },
      radii: {
        stage: { value: "32px" },
        card: { value: "24px" },
        soft: { value: "18px" },
        pill: { value: "999px" },
      },
      shadows: {
        pressable: { value: "0 6px 0 #B71C1C" },
        pressableHover: { value: "0 8px 0 #991B1B" },
        pressableActive: { value: "0 2px 0 #7F1D1D" },
        cardLift: { value: "0 16px 40px rgba(0,0,0,0.12)" },
        glow: { value: "0 12px 30px rgba(229,57,53,0.18)" },
      },
      colors: {
        brand: {
          bg: {
            pane: { value: "{colors.gray.800}" },
            surface: { value: "{colors.gray.700}" },
            detail: { value: "{colors.gray.600}" },
          },
        },
        arawaks: {
          skyBlue:        { value: "#29B6F6" },
          jungleGreen:    { value: "#4CAF50" },
          sunYellow:      { value: "#FFD54F" },
          adventureOrange:{ value: "#FF8A3D" },
          grapePurple:    { value: "#8E5CF7" },
          campfireRed:    { value: "#E53935" },
          rose:           { value: "#EC4899" },
          sunshineYellow: { value: "#FDD835" },
          tropicalTeal:   { value: "#00BFA5" },
          playfulIndigo:  { value: "#5C6BC0" },
          clayOrange:     { value: "#F4511E" },
          forestGreen:    { value: "#43A047" },
          goldenYellow:   { value: "#FFC107" },
          lagoonTeal:     { value: "#26A69A" },
          orchidPurple:   { value: "#AB47BC" },
          treeBarkBrown:  { value: "#6D4C41" },
          blueGray:       { value: "#607D8B" },
        },
      },
    },
    semanticTokens: {
      colors: {
        brand: {
          red: {
            value: "#da2c27",
          },
          redFg: {
            value: { _light: "#b92722", _dark: "#f0625b" },
          },
          redSubtle: {
            value: { _light: "#f9e3e1", _dark: "#4a2629" },
          },
          redBorder: {
            value: { _light: "#e7a09b", _dark: "#814047" },
          },
          redHover: {
            value: "#c72521",
          },
          redActive: {
            value: "#a3211d",
          },
        },
        public: {
          bg: {
            canvas: { value: { _light: "#f1f3f6", _dark: "#171a21" } },
            panel: { value: { _light: "#f8f9fb", _dark: "#222733" } },
            surface: { value: { _light: "#eef1f5", _dark: "#292f3b" } },
            muted: { value: { _light: "#e6eaf0", _dark: "#303745" } },
            elevated: { value: { _light: "#f8f9fb", _dark: "#252b36" } },
            footer: { value: { _light: "#18222d", _dark: "#111720" } },
          },
          fg: {
            default: { value: { _light: "#2d3748", _dark: "#edf0f5" } },
            muted: { value: { _light: "#5d6675", _dark: "#b8c0ce" } },
            subtle: { value: { _light: "#7a8494", _dark: "#8f99a9" } },
          },
          border: {
            default: { value: { _light: "#cbd2dc", _dark: "#485164" } },
            subtle: { value: { _light: "#dfe3e9", _dark: "#343c4a" } },
          },
        },
        design: {
          bg: {
            canvas: {
              value: { _light: "{colors.orange.50}", _dark: "{colors.gray.950}" },
            },
            panel: {
              value: { _light: "{colors.white}", _dark: "{colors.gray.900}" },
            },
            surface: {
              value: { _light: "{colors.white}", _dark: "{colors.gray.800}" },
            },
            muted: {
              value: { _light: "{colors.gray.50}", _dark: "{colors.gray.700}" },
            },
            elevated: {
              value: { _light: "{colors.white}", _dark: "{colors.gray.900}" },
            },
          },
          fg: {
            default: {
              value: { _light: "{colors.gray.800}", _dark: "{colors.gray.100}" },
            },
            muted: {
              value: { _light: "{colors.gray.600}", _dark: "{colors.gray.400}" },
            },
            subtle: {
              value: { _light: "{colors.gray.500}", _dark: "{colors.gray.500}" },
            },
            inverse: {
              value: { _light: "{colors.white}", _dark: "{colors.gray.950}" },
            },
          },
          border: {
            default: {
              value: { _light: "{colors.gray.200}", _dark: "{colors.gray.700}" },
            },
            subtle: {
              value: { _light: "{colors.gray.100}", _dark: "{colors.gray.800}" },
            },
          },
          overlay: {
            image: {
              value: { _light: "rgba(10,0,0,0.70)", _dark: "rgba(10,0,0,0.70)" },
            },
          },
        },
        dashboard: {
          bg: {
            canvas: {
              value: {
                _light: "{colors.gray.50}",
                _dark: "var(--dashboard-bg-canvas, var(--chakra-colors-gray-950))",
              },
            },
            panel: {
              value: {
                _light: "{colors.white}",
                _dark: "var(--dashboard-bg-panel, var(--chakra-colors-gray-900))",
              },
            },
            surface: {
              value: {
                _light: "{colors.white}",
                _dark: "var(--dashboard-bg-surface, var(--chakra-colors-gray-800))",
              },
            },
            surfaceMuted: {
              value: {
                _light: "{colors.gray.100}",
                _dark: "var(--dashboard-bg-surface-muted, var(--chakra-colors-gray-700))",
              },
            },
            surfaceSubtle: {
              value: {
                _light: "{colors.gray.50}",
                _dark: "var(--dashboard-bg-surface-subtle, var(--chakra-colors-gray-600))",
              },
            },
            dialog: {
              value: {
                _light: "{colors.white}",
                _dark: "var(--dashboard-bg-dialog, var(--chakra-colors-gray-800))",
              },
            },
            input: {
              value: {
                _light: "{colors.white}",
                _dark: "var(--dashboard-bg-input, var(--chakra-colors-gray-700))",
              },
            },
            popover: {
              value: {
                _light: "{colors.white}",
                _dark: "var(--dashboard-bg-popover, var(--chakra-colors-gray-700))",
              },
            },
            tabList: {
              value: {
                _light: "{colors.gray.100}",
                _dark: "var(--dashboard-bg-tab-list, var(--chakra-colors-gray-700))",
              },
            },
            tabHover: {
              value: {
                _light: "{colors.blackAlpha.50}",
                _dark: "var(--dashboard-bg-tab-hover, var(--chakra-colors-whiteAlpha-200))",
              },
            },
            tabSelected: {
              value: {
                _light: "{colors.red.100}",
                _dark: "var(--dashboard-bg-tab-selected, var(--chakra-colors-gray-600))",
              },
            },
            choiceSelected: {
              value: {
                _light: "{colors.gray.200}",
                _dark: "var(--dashboard-bg-choice-selected, var(--chakra-colors-gray-600))",
              },
            },
          },
            fg: {
              default: {
                value: {
                  _light: "{colors.gray.800}",
                  _dark: "var(--dashboard-fg-default, var(--chakra-colors-gray-200))",
                },
              },
              muted: {
                value: {
                  _light: "{colors.gray.600}",
                  _dark: "var(--dashboard-fg-muted, var(--chakra-colors-gray-400))",
                },
              },
              subtle: {
                value: {
                  _light: "{colors.gray.500}",
                  _dark: "var(--dashboard-fg-subtle, var(--chakra-colors-gray-500))",
                },
              },
            onAccent: {
              value: { _light: "{colors.white}", _dark: "{colors.white}" },
            },
          },
          border: {
            default: {
              value: {
                _light: "{colors.gray.200}",
                _dark: "var(--dashboard-border-default, var(--chakra-colors-gray-500))",
              },
            },
            muted: {
              value: {
                _light: "{colors.gray.100}",
                _dark: "var(--dashboard-border-muted, var(--chakra-colors-gray-600))",
              },
            },
          },
          accent: {
            solid: {
              value: { _light: "{colors.brand.red}", _dark: "{colors.red.400}" },
            },
            fg: {
              value: { _light: "{colors.brand.red}", _dark: "{colors.brand.redFg}" },
            },
            subtle: {
              value: "{colors.brand.redSubtle}",
            },
            border: {
              value: { _light: "{colors.brand.red}", _dark: "{colors.red.300}" },
            },
          },
          feedback: {
            error: {
              value: { _light: "{colors.brand.red}", _dark: "{colors.red.300}" },
            },
          },
          tag: {
            bg: {
              value: {
                _light: "{colors.gray.100}",
                _dark: "var(--dashboard-tag-bg, var(--chakra-colors-gray-600))",
              },
            },
            fg: {
              value: {
                _light: "{colors.gray.700}",
                _dark: "var(--dashboard-tag-fg, var(--chakra-colors-white))",
              },
            },
            border: {
              value: {
                _light: "{colors.gray.300}",
                _dark: "var(--dashboard-tag-border, var(--chakra-colors-gray-400))",
              },
            },
          },
          metric: {
            info: {
              bg:   { value: { _light: "{colors.arawaks.skyBlue}",         _dark: "{colors.arawaks.skyBlue}" } },
              fg:   { value: { _light: "{colors.white}",                   _dark: "{colors.white}" } },
              icon: { value: { _light: "{colors.white}",                   _dark: "{colors.white}" } },
            },
            success: {
              bg:   { value: { _light: "{colors.arawaks.jungleGreen}",     _dark: "{colors.arawaks.jungleGreen}" } },
              fg:   { value: { _light: "{colors.white}",                   _dark: "{colors.white}" } },
              icon: { value: { _light: "{colors.white}",                   _dark: "{colors.white}" } },
            },
            warning: {
              bg:   { value: { _light: "{colors.arawaks.adventureOrange}", _dark: "{colors.arawaks.adventureOrange}" } },
              fg:   { value: { _light: "{colors.white}",                   _dark: "{colors.white}" } },
              icon: { value: { _light: "{colors.white}",                   _dark: "{colors.white}" } },
            },
            danger: {
              bg:   { value: { _light: "{colors.arawaks.campfireRed}",     _dark: "{colors.arawaks.campfireRed}" } },
              fg:   { value: { _light: "{colors.white}",                   _dark: "{colors.white}" } },
              icon: { value: { _light: "{colors.white}",                   _dark: "{colors.white}" } },
            },
          },
        },
      },
    },
    recipes: {
      brandButton: pressableButtonRecipe,
    },
  },
});

export default createSystem(defaultConfig, config);
